import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { NotifyPreview, NotifyTemplateKey, NotifyTemplateMeta } from "@breeze/shared";
import { fetchNotifyPreview, fetchNotifyTemplates, sendMessage } from "../api.js";

type Props = {
  open: boolean;
  onClose: () => void;
  vid: number;
  cardId: number;
  orderLabel: string;
  customerName: string;
};

export function NotifyModal({
  open,
  onClose,
  vid,
  cardId,
  orderLabel,
  customerName,
}: Props): JSX.Element {
  const [templates, setTemplates] = useState<NotifyTemplateMeta[]>([]);
  const [selectedKey, setSelectedKey] = useState<NotifyTemplateKey | "">("");
  const [preview, setPreview] = useState<NotifyPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSent(false);
    setSendError(null);
    fetchNotifyTemplates()
      .then((list) => {
        if (!cancelled) setTemplates(list);
      })
      .catch(() => {
        // Non-fatal — the modal still opens with an empty dropdown.
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Reset fields when modal closes so reopening gives a clean slate.
  useEffect(() => {
    if (!open) {
      setSelectedKey("");
      setPreview(null);
      setSubject("");
      setBody("");
      setTo("");
      setSendError(null);
      setSent(false);
    }
  }, [open]);

  // When a template is picked, fetch its preview and populate editable fields.
  useEffect(() => {
    if (!open || !selectedKey) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    fetchNotifyPreview(vid, selectedKey)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        setSubject(p.subject ?? "");
        setBody(p.bodyText);
        setTo(p.to);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPreviewError(err instanceof Error ? err.message : "preview failed");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, vid, selectedKey]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const grouped = useMemo(() => {
    const out: Record<string, NotifyTemplateMeta[]> = {};
    for (const t of templates) {
      (out[t.group] ??= []).push(t);
    }
    return out;
  }, [templates]);

  const canSend =
    preview !== null &&
    to.trim().length > 0 &&
    body.trim().length > 0 &&
    !sending;

  // WA Content-template sends require the rendered body to match exactly —
  // any staff edit means we can't use the template (Twilio would still send
  // the template verbatim), so drop contentSid and let WF-OUT fall back to
  // freeform. Freeform only works inside the 24h session window.
  const useTemplate =
    preview !== null &&
    preview.contentSid !== null &&
    body === preview.bodyText;

  const handleSend = async (): Promise<void> => {
    if (!preview || !canSend) return;
    setSending(true);
    setSendError(null);
    try {
      await sendMessage({
        cardId,
        channel: preview.channel,
        to: to.trim(),
        ...(preview.channel === "email" && subject.trim()
          ? { subject: subject.trim() }
          : {}),
        bodyText: body,
        ...(useTemplate && preview.contentSid
          ? {
              contentSid: preview.contentSid,
              contentVariables: preview.contentVariables ?? {},
            }
          : {}),
      });
      setSent(true);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[80] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="notify"
            className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-2xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-start justify-between gap-3 border-b border-ink-700 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-50">
                    Send notification
                  </div>
                  <h3 className="mt-1 text-xl leading-tight text-ink-50">
                    {orderLabel} — {customerName}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>

              <div className="space-y-3 px-5 py-4 text-[13px]">
                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    Template
                  </span>
                  <select
                    value={selectedKey}
                    onChange={(e) => setSelectedKey(e.target.value as NotifyTemplateKey)}
                    className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
                  >
                    <option value="">— Select a template —</option>
                    {Object.entries(grouped).map(([group, list]) => (
                      <optgroup key={group} label={group}>
                        {list.map((t) => (
                          <option key={t.key} value={t.key}>
                            {t.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>

                {previewLoading ? (
                  <div className="rounded-md border border-ink-700 bg-ink-900/40 px-3 py-6 text-center text-ink-200">
                    rendering preview…
                  </div>
                ) : previewError ? (
                  <div className="rounded-md border border-slack-pink/40 bg-slack-pink/10 px-3 py-2 text-slack-pink">
                    {previewError}
                  </div>
                ) : preview ? (
                  <>
                    <label className="block">
                      <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                        {preview.channel === "email" ? "To (email)" : "To (mobile)"}
                      </span>
                      <input
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder={preview.channel === "email" ? "name@example.com" : "+447…"}
                        className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
                      />
                    </label>

                    {preview.channel === "email" ? (
                      <label className="block">
                        <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                          Subject
                        </span>
                        <input
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50"
                        />
                      </label>
                    ) : null}

                    <label className="block">
                      <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                        Message
                      </span>
                      <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={
                          preview.channel === "sms"
                            ? 5
                            : preview.channel === "whatsapp"
                              ? 8
                              : 10
                        }
                        className="w-full rounded-md border border-ink-500 bg-ink-900 px-2.5 py-2 text-[13px] leading-snug text-ink-50"
                      />
                      {preview.channel === "email" ? (
                        <span className="mt-1 block text-[12px] text-ink-200">
                          Signature is appended automatically on send.
                        </span>
                      ) : preview.channel === "whatsapp" ? (
                        <div className="mt-1 space-y-1">
                          {useTemplate ? (
                            <span className="block text-[12px] text-ink-200">
                              Sending as an approved Twilio template — works outside the 24-hour window.
                            </span>
                          ) : (
                            <span className="block text-[12px] text-slack-yellow">
                              You've edited the text, so this will send as a freeform WhatsApp message — only works inside the 24-hour conversation window.
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="mt-1 block text-[12px] text-ink-200 tabular-nums">
                          {body.length} chars
                        </span>
                      )}
                    </label>
                  </>
                ) : (
                  <div className="rounded-md border border-dashed border-ink-500 bg-ink-900/40 px-3 py-6 text-center text-ink-200">
                    Pick a template to preview.
                  </div>
                )}
              </div>

              <footer className="flex items-center justify-between gap-2 border-t border-ink-700 px-5 py-3">
                <div className="text-[13px]">
                  {sent ? (
                    <span className="text-slack-green">Sent — check Timeline.</span>
                  ) : sendError ? (
                    <span className="text-slack-pink">⚠ {sendError}</span>
                  ) : (
                    <span className="text-ink-200">
                      Sends via the kanban pipeline and logs on this card.
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:border-ink-400"
                  >
                    {sent ? "Close" : "Cancel"}
                  </button>
                  <button
                    onClick={() => void handleSend()}
                    disabled={!canSend || sent}
                    className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {sending ? "Sending…" : sent ? "Sent" : "Send"}
                  </button>
                </div>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
